import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useIncidenciasPanel from '../../hooks/useIncidenciasPanel';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const ESTADOS = [
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'en_progreso', etiqueta: 'En proceso' },
  { valor: 'culminado', etiqueta: 'Culminar' },
  { valor: 'anulado', etiqueta: 'Anular' },
];

const SEVERIDADES = [
  { valor: 'baja', etiqueta: 'Baja' },
  { valor: 'media', etiqueta: 'Media' },
  { valor: 'alta', etiqueta: 'Alta' },
  { valor: 'critica', etiqueta: 'Crítica' },
];

const TABS = [
  { id: 'detalle', etiqueta: 'Detalle', icono: 'fa-file-lines' },
  { id: 'evidencia', etiqueta: 'Evidencia', icono: 'fa-photo-film' },
  { id: 'respuesta', etiqueta: 'Respuesta y estado', icono: 'fa-reply' },
  { id: 'bitacora', etiqueta: 'Bitácora', icono: 'fa-clock-rotate-left' },
];

// Estados finales — el backend ya rechaza cualquier transición desde acá,
// el conmutador solo evita ofrecer un botón que sabemos que va a fallar.
const ES_ESTADO_FINAL = (estado) => estado === 'culminado' || estado === 'anulado';

const ICONO_EVIDENCIA = { Imagen: 'fa-image', Video: 'fa-video', Audio: 'fa-microphone' };

// Mismos íconos que ya usa el dashboard (`AlertasDelDia.jsx`) para cada
// estado — un ticket "pendiente" se identifica con el mismo ícono en toda
// la consola, nunca uno distinto según la pantalla.
const ICONO_POR_ESTADO_RESPUESTA = {
  pendiente: 'fa-clock',
  en_progreso: 'fa-spinner',
  culminado: 'fa-circle-check',
  anulado: 'fa-ban',
};

const formatearHora = (valorHora) => (valorHora ? valorHora.slice(0, 5) : null);

// Fecha/hora "ocurrió" en dos piezas — el modelo las separa
// (`inc_dtFechaIncidente`/`inc_tmHoraIncidente`), la bitácora los junta en
// un timestamp, así que cada uno arma su propio par fecha/hora.
const ocurrioFechaHora = (ticket) => {
  if (!ticket.inc_dtFechaIncidente) return { fecha: '—', hora: '—' };
  const fecha = new Date(`${ticket.inc_dtFechaIncidente}T00:00:00`).toLocaleDateString('es-PE');
  return { fecha, hora: formatearHora(ticket.inc_tmHoraIncidente) || '—' };
};

const creadoFechaHora = (ticket) => {
  if (!ticket.fecha_creacion_formateada) return { fecha: '—', hora: '—' };
  const [fecha, hora] = ticket.fecha_creacion_formateada.split(' ');
  return { fecha, hora };
};

const bitacoraFechaHora = (isoString) => {
  const dt = new Date(isoString);
  return { fecha: dt.toLocaleDateString('es-PE'), hora: dt.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }) };
};

/**
 * Página completa del detalle de un ticket (README 7.4) — ruta propia
 * `/panel/tickets/:id`, con pestañas (Detalle / Evidencia / Respuesta y
 * estado / Bitácora) y un botón "Volver a Incidencias" para regresar al
 * listado. No es un panel embebido dentro de la tabla — es un formulario
 * avanzado en su propia pantalla, tal como se pidió: responder, analizar
 * la situación con la evidencia completa y revisar el histórico.
 */
const TicketDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, error, obtenerTicket, guardarTicket, obtenerBitacora } = useIncidenciasPanel();

  const [tab, setTab] = useState('detalle');
  const [ticket, setTicket] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [estadoEditado, setEstadoEditado] = useState(null);
  const [severidadEditada, setSeveridadEditada] = useState(null);
  const [respuestaEditada, setRespuestaEditada] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [modal, setModal] = useState(null); // evidencia abierta en el modal, o null
  const [mapaAbierto, setMapaAbierto] = useState(false);
  const modalRef = useRef(null);

  const cargar = useCallback(async () => {
    const [resultadoTicket, resultadoBitacora] = await Promise.all([
      obtenerTicket(id),
      obtenerBitacora(id),
    ]);
    if (resultadoTicket.success) {
      setTicket(resultadoTicket.ticket);
      setEstadoEditado(resultadoTicket.ticket.inc_chStatus);
      setSeveridadEditada(resultadoTicket.ticket.inc_chSeveridad);
      setRespuestaEditada(resultadoTicket.ticket.inc_txRespuestaPublica || '');
    }
    if (resultadoBitacora.success) setBitacora(resultadoBitacora.historial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { setMensaje(''); cargar(); }, [cargar]);

  const guardar = async () => {
    setMensaje('');
    const resultado = await guardarTicket(id, {
      inc_chStatus: estadoEditado,
      inc_chSeveridad: severidadEditada,
      inc_txRespuestaPublica: respuestaEditada,
    });
    if (resultado.success) {
      setMensaje('Cambios guardados.');
      cargar();
    } else {
      setMensaje(resultado.error);
    }
  };

  const alternarPantallaCompleta = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      modalRef.current?.requestFullscreen?.();
    }
  };

  if (loading && !ticket) {
    return <PanelShell><p className="panel-texto-ayuda">Cargando ticket…</p></PanelShell>;
  }

  if (!ticket) {
    return (
      <PanelShell>
        {error && <p className="panel-gu-mensaje">{error}</p>}
        <button type="button" className="panel-boton-secundario" onClick={() => navigate('/panel/tickets')}>
          Volver a Incidencias
        </button>
      </PanelShell>
    );
  }

  const ocurrio = ocurrioFechaHora(ticket);
  const creado = creadoFechaHora(ticket);
  // Anónimo solo limpia documento y apellidos (ver Incidencia.clean) — el
  // alias, teléfono y correo se conservan si el denunciante los dejó.
  const tieneContacto = ticket.inc_chNombre || ticket.inc_chTelefono || ticket.inc_chEmail;

  return (
    <PanelShell>
      <button type="button" className="panel-boton-secundario" style={{ marginBottom: 16 }} onClick={() => navigate('/panel/tickets')}>
        <i className="fas fa-arrow-left"></i> Volver a Incidencias
      </button>

      <div className="panel-ticket-panel-cabecera">
        <div>
          <span className="panel-datatable-codigo">{ticket.inc_chTiket}</span>
          <h2 style={{ margin: '4px 0 2px' }}>{ticket.fk_categoria?.cat_chNombre || 'Sin categoría'}</h2>
          <p className="panel-texto-ayuda" style={{ margin: 0 }}>{ticket.inc_chDireccion || 'Sin dirección'}</p>
        </div>
        <span className={`pildora-estado pildora-estado-${ticket.inc_chStatus}`} style={{ fontSize: 12 }}>
          {ticket.inc_chStatus_display}
        </span>
      </div>

      {mensaje && <p className="panel-gu-mensaje">{mensaje}</p>}
      {error && <p className="panel-gu-mensaje">{error}</p>}

      <div className="panel-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`panel-tab ${tab === t.id ? 'panel-tab-activa' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <i className={`fas ${t.icono}`}></i> {t.etiqueta}
          </button>
        ))}
      </div>

      {tab === 'detalle' && (
        <div className="panel-ticket-panel">
          {/* Grid 2x2 (HU-012): izquierda todo lo del lugar/hecho en sí,
              derecha el denunciante — antes eran 5 campos en un grid parejo
              donde 3 ocupaban el ancho completo, forzando scroll vertical
              para ver el denunciante. Ahora son dos columnas independientes
              que crecen cada una a su propio alto. */}
          <div className="panel-ticket-detalle-grid">
            <div className="panel-ticket-detalle-col">
              <div className="panel-ticket-info-campo">
                <span className="panel-label">Dirección</span>
                <div className="panel-ticket-info-direccion">
                  <span>{ticket.inc_chDireccion || 'Sin dirección registrada'}</span>
                  {ticket.inc_deLatitud && ticket.inc_deLongitud && (
                    <button type="button" className="panel-boton-secundario panel-ticket-boton-mapa" onClick={() => setMapaAbierto(true)}>
                      <i className="fas fa-location-dot"></i> Ver mapa
                    </button>
                  )}
                </div>
                {ticket.inc_chReferencia && <span className="panel-texto-ayuda">{ticket.inc_chReferencia}</span>}
              </div>

              <div className="panel-ticket-info-campo-par">
                <div className="panel-ticket-info-campo">
                  <span className="panel-label">Ocurrió</span>
                  <span>{ocurrio.fecha} · {ocurrio.hora}</span>
                </div>
                <div className="panel-ticket-info-campo">
                  <span className="panel-label">Ingresó al sistema</span>
                  <span>{creado.fecha} · {creado.hora}</span>
                </div>
              </div>

              <div className="panel-ticket-info-campo">
                <span className="panel-label">Descripción del ciudadano</span>
                <p className="panel-ticket-info-descripcion">{ticket.inc_txComentario || 'Sin descripción adicional.'}</p>
              </div>
            </div>

            <div className="panel-ticket-detalle-col">
              <div className="panel-ticket-info-campo">
                <span className="panel-label">Denunciante</span>
                {ticket.inc_boAnonimo && <span className="panel-texto-ayuda panel-ticket-anonimo-aviso">Denuncia anónima — datos de identidad no solicitados.</span>}
                {tieneContacto ? (
                  <div className="panel-ticket-denunciante">
                    <p><strong>{ticket.inc_boAnonimo ? (ticket.inc_chNombre || 'Alias no registrado') : ticket.nombre_completo}</strong></p>
                    {!ticket.inc_boAnonimo && ticket.inc_chNumDoc && <p>Documento: {ticket.inc_chNumDoc}</p>}
                    {ticket.inc_chTelefono && <p>Teléfono: {ticket.inc_chTelefono}</p>}
                    {ticket.inc_chEmail && <p>Correo: {ticket.inc_chEmail}</p>}
                  </div>
                ) : (
                  <p className="panel-texto-ayuda">Sin datos de contacto.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'evidencia' && (
        <div className="panel-ticket-panel">
          <span className="panel-label">Evidencia ({ticket.evidencia.length})</span>
          {ticket.evidencia.length === 0 ? (
            <p className="panel-texto-ayuda">Sin evidencia adjunta.</p>
          ) : (
            <div className="panel-evidencia-grid">
              {ticket.evidencia.map((ev) => (
                <button key={ev.arc_inId} type="button" className="panel-evidencia-item panel-evidencia-item-boton" onClick={() => setModal(ev)}>
                  {ev.arc_chTipoArchivo === 'Imagen' ? <img src={ev.url_descarga} alt={ev.arc_chNombre} /> : <i className={`fas ${ICONO_EVIDENCIA[ev.arc_chTipoArchivo] || 'fa-file'}`}></i>}
                  <span>{ev.arc_chNombre}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'respuesta' && (
        <div className="panel-ticket-panel">
          {/* Grid 2 columnas (HU-012): izquierda los conmutadores de estado/
              severidad como tarjetas de color, derecha la caja de respuesta
              — antes todo iba apilado en una sola columna larga. */}
          <div className="panel-ticket-respuesta-grid">
            <div>
              <span className="panel-label">Cambiar estado</span>
              <div className="panel-conmutador-estado panel-conmutador-tarjetas">
                {ESTADOS.map((e) => {
                  const deshabilitado = ES_ESTADO_FINAL(ticket.inc_chStatus) && e.valor !== ticket.inc_chStatus;
                  return (
                    <button
                      key={e.valor}
                      type="button"
                      disabled={deshabilitado}
                      className={`panel-conmutador-boton ${estadoEditado === e.valor ? `panel-conmutador-boton-activo-${e.valor}` : ''}`}
                      onClick={() => setEstadoEditado(e.valor)}
                    >
                      <i className={`fas ${ICONO_POR_ESTADO_RESPUESTA[e.valor]}`}></i>
                      {e.etiqueta}
                    </button>
                  );
                })}
              </div>

              <span className="panel-label" style={{ marginTop: 20 }}>Severidad</span>
              <div className="panel-conmutador-estado panel-conmutador-tarjetas">
                {SEVERIDADES.map((s) => (
                  <button
                    key={s.valor}
                    type="button"
                    className={`panel-conmutador-boton ${severidadEditada === s.valor ? `panel-conmutador-boton-activo-severidad-${s.valor}` : ''}`}
                    onClick={() => setSeveridadEditada(s.valor)}
                  >
                    <i className="fas fa-signal"></i>
                    {s.etiqueta}
                  </button>
                ))}
              </div>
            </div>

            <div className="panel-ticket-respuesta-col">
              <span className="panel-label">Respuesta visible en el ticket público</span>
              <textarea
                className="panel-input panel-ticket-respuesta"
                rows={8}
                placeholder="Escribe la respuesta que verá el ciudadano en su ticket…"
                value={respuestaEditada}
                onChange={(e) => setRespuestaEditada(e.target.value)}
              />

              <button type="button" className="panel-boton-primario" style={{ maxWidth: 220, marginTop: 16 }} onClick={guardar} disabled={loading}>
                {loading ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'bitacora' && (
        <div className="panel-ticket-panel">
          <span className="panel-label">Bitácora</span>
          {bitacora.length === 0 ? (
            <p className="panel-texto-ayuda">Sin movimientos registrados todavía.</p>
          ) : (
            <ul className="panel-bitacora-lista">
              {bitacora.map((item) => {
                const { fecha, hora } = bitacoraFechaHora(item.his_dtFechaAccion);
                return (
                  <li key={item.his_inId} className="panel-bitacora-item">
                    <div className="panel-bitacora-punto"></div>
                    <div className="panel-bitacora-cuerpo">
                      <div className="panel-bitacora-accion">{item.accion_display || item.resumen_accion}</div>
                      {item.his_txDescripcion && <div className="panel-texto-ayuda">{item.his_txDescripcion}</div>}
                      <div className="panel-bitacora-meta">{item.usuario_nombre || 'Sistema'}</div>
                    </div>
                    <div className="panel-bitacora-fecha">
                      <span>{fecha}</span>
                      <span>{hora}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {modal && (
        <div className="panel-lightbox-fondo" onClick={() => setModal(null)}>
          <div className="panel-lightbox-contenido" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="panel-lightbox-barra">
              <span>{modal.arc_chNombre}</span>
              <div>
                <button type="button" className="panel-lightbox-accion" onClick={alternarPantallaCompleta} title="Pantalla completa">
                  <i className="fas fa-expand"></i>
                </button>
                <button type="button" className="panel-lightbox-accion" onClick={() => setModal(null)} title="Cerrar">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            {modal.arc_chTipoArchivo === 'Imagen' && <img src={modal.url_descarga} alt={modal.arc_chNombre} />}
            {modal.arc_chTipoArchivo === 'Video' && <video src={modal.url_descarga} controls autoPlay />}
            {modal.arc_chTipoArchivo === 'Audio' && <audio src={modal.url_descarga} controls autoPlay style={{ width: '100%' }} />}
            {!['Imagen', 'Video', 'Audio'].includes(modal.arc_chTipoArchivo) && (
              <a href={modal.url_descarga} target="_blank" rel="noreferrer" className="panel-boton-primario" style={{ display: 'inline-block' }}>
                Descargar {modal.arc_chNombre}
              </a>
            )}
          </div>
        </div>
      )}

      {mapaAbierto && ticket.inc_deLatitud && ticket.inc_deLongitud && (
        <div className="panel-lightbox-fondo" onClick={() => setMapaAbierto(false)}>
          <div className="panel-lightbox-contenido panel-lightbox-mapa" onClick={(e) => e.stopPropagation()}>
            <div className="panel-lightbox-barra">
              <span>{ticket.inc_chDireccion || 'Ubicación de la incidencia'}</span>
              <button type="button" className="panel-lightbox-accion" onClick={() => setMapaAbierto(false)} title="Cerrar">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <iframe
              title="Ubicación de la incidencia"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(ticket.inc_deLongitud) - 0.004}%2C${Number(ticket.inc_deLatitud) - 0.003}%2C${Number(ticket.inc_deLongitud) + 0.004}%2C${Number(ticket.inc_deLatitud) + 0.003}&marker=${ticket.inc_deLatitud}%2C${ticket.inc_deLongitud}`}
            />
          </div>
        </div>
      )}
    </PanelShell>
  );
};

export default TicketDetalle;
